import React from "react";
import type { TicketDetail } from "../../../../api/tickets";

type TicketStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";

type TicketHeaderProps = {
  ticket: TicketDetail;
  onStartWork: () => void;
  onResolve: () => void;
  onClose: () => void;
  onReopen: () => void;
};

const statusStyles: Record<TicketStatus, string> = {
  OPEN: "bg-amber-100 text-amber-800",
  IN_PROGRESS: "bg-blue-100 text-blue-800",
  RESOLVED: "bg-emerald-100 text-emerald-800",
  CLOSED: "bg-slate-200 text-slate-700",
};

const severityTone = (value: number) => {
  if (value >= 3) return "bg-red-100 text-red-800";
  if (value === 2) return "bg-orange-100 text-orange-800";
  return "bg-yellow-100 text-yellow-800";
};

export default function TicketHeader({ ticket, onStartWork, onResolve, onClose, onReopen }: TicketHeaderProps) {
  const formattedOpened = new Date(ticket.opened_at).toLocaleString();

  return (
    <section className="bg-white shadow-sm rounded-lg border border-slate-200">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold text-slate-900">Ticket #{ticket.id}</h1>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusStyles[ticket.status as TicketStatus]}`}>
              {ticket.status}
            </span>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${severityTone(ticket.severity_opened || 0)}`}>
              Severity {ticket.severity_opened}
            </span>
            <span className="text-xs text-slate-500">Opened {formattedOpened}</span>
            {ticket.created_by && <span className="text-xs text-slate-500">• Created by {ticket.created_by}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onStartWork}
            className="px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-200"
            disabled={ticket.status !== "OPEN"}
          >
            Start Work
          </button>
          <button
            onClick={onResolve}
            className="px-3 py-2 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-emerald-200"
            disabled={ticket.status !== "IN_PROGRESS"}
          >
            Resolve
          </button>
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm font-medium rounded-md bg-slate-700 text-white hover:bg-slate-800 disabled:bg-slate-300"
            disabled={!["RESOLVED", "IN_PROGRESS"].includes(ticket.status as TicketStatus)}
          >
            Close
          </button>
          <button
            onClick={onReopen}
            className="px-3 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:bg-amber-200"
            disabled={ticket.status !== "CLOSED"}
          >
            Reopen
          </button>
        </div>
      </div>
    </section>
  );
}
